import { FormEvent, useCallback, useEffect, useRef, useState } from 'react'

import { clsx } from 'clsx'
import { Error, IconSearch, IconSpin, IconXmarkLight } from 'packages/component-library/dist'
import { useTranslation } from 'react-i18next'

import { SelectProductMobile } from './SelectProduct/mobile'
import appDefaults from '~/portal/portal.defaults.json'
import { SEARCH_TAB_TYPE } from '~/portal/types/search'
import { SelectItem } from '~/portal/types/ui.components'

export type SearchProductsMobileProps = {
  onSearch: (query: string, type: SEARCH_TAB_TYPE, productId?: string) => void;
  allProductsLabel: string;
  initialText?: string;
  accentColor?: string;
  onChange?: (text: string) => void;
  isLoading?: boolean;
  onInputLengthChange?: (length: number) => void;
  products?: Array<{ id: string; name: string }>;
  currentProduct?: { id: string; name: string };
  tab: SEARCH_TAB_TYPE;
  handleClear: () => void;
}

export const SearchProductsMobile = ({
  initialText = '',
  accentColor = '',
  onSearch,
  onChange,
  isLoading = false,
  onInputLengthChange,
  products = [],
  currentProduct,
  allProductsLabel,
  tab,
  handleClear
}: SearchProductsMobileProps) => {
  const { t } = useTranslation()
  const [text, setText] = useState<string>(initialText)
  const [prevInputValue, setPrevInputValue] = useState('')

  const selectOptions = [{ id: allProductsLabel, name: allProductsLabel }, ...products]

  const [selectedProduct, setSelectedProduct] = useState<SelectItem>(currentProduct || selectOptions[0])
  const textRef = useRef(text)
  
  useEffect(() => {
    onChange?.(text)
    textRef.current = text 
  }, [text])

  useEffect(() => {
    if (selectedProduct.id === allProductsLabel) {
      onSearch(text, tab)
    } else {
      onSearch(text, tab, selectedProduct.id)
    }
  }, [tab])
  
  const handleInputChange = useCallback((event: FormEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget
    setText(value)
    onInputLengthChange?.(value?.trim().length)

    if (value && value !== prevInputValue) {
      if (selectedProduct.id === allProductsLabel) {
        onSearch(value, tab)
      } else {
        onSearch(value, tab, selectedProduct.id)
      }
      setPrevInputValue(value)
    }
    
  }, [onInputLengthChange, selectedProduct])

  const onClear = useCallback(() => {
    setText('')
    handleClear()
  }, [onInputLengthChange])

  const onChangeProduct = (product: SelectItem) => {
    setSelectedProduct(product)
    if (product.id === allProductsLabel) {
      onSearch(textRef.current, tab)
    } else {
      onSearch(textRef.current, tab, product.id)
    }
  }

  const maxLengthInput = text.length > appDefaults.search.maxQueryLength

  return (
    <div className="flex flex-col pb-4">
      <div
        className={clsx(
          'sp-search-input relative w-full rounded-md mb-3',
          'flex items-center rounded border border-(--sp-color-border-strong) w-full text-(--sp-color-text)',
          'dark:bg-(--sp-color-surface)',
          'hover:border-(--sp-color-accent) focus:border-(--sp-color-accent)',
          maxLengthInput && 'border-error-30!'
        )}
        data-testid="search"

      >
        <div className="flex items-center ml-3 mr-2 text-(--sp-color-text-muted)">
          <IconSearch width={16} height={16} />
        </div>
        <input
          aria-label="search input"
          type="text"
          value={text}
          onInput={handleInputChange}
          className="flex-grow block w-full py-2 border-0 text-(--sp-color-text) focus:ring-0 text-body2 dark:bg-transparent"
        />
        {isLoading ? (
          <div
            className="flex items-center pl-2 pr-3"
            data-testid="search-loading"
          >
            <IconSpin width={16} height={16} className="text-(--sp-color-link) dark:text-(--sp-color-link)" />
          </div>
        ) : (
          <div className="flex items-center ml-2 mr-3 cursor-pointer text-(--sp-color-text-muted) hover:text-(--sp-color-text)">
            <div aria-label="search clean" onClick={onClear} className="flex items-center justify-center w-4 h-4 ">
              <IconXmarkLight />
            </div>
          </div>
        )}
      </div>
      {maxLengthInput && (
        <div className="pb-3" data-testid="search-max-length-error">
          <Error>{t('components.ui.searchModal.maxLengthError')}</Error>
        </div>
      )}
      <SelectProductMobile
        options={selectOptions}
        accentColor={accentColor}
        defaultValue={currentProduct ? currentProduct : selectOptions[0]}
        onChange={onChangeProduct}
      />
    </div>
  )
}